import { Box, Typography } from '@mui/material';
import { INVALID_INPUT_MESSAGE } from 'utils/apiErrorMessage';

// 複数要素を 1 リクエストで処理する API（コピー登録 / 達成度計算）の部分失敗を
// 「どの要素が・なぜ失敗したか」として一覧表示する共通コンポーネント。
// R は {type, gear}、L は {unit_id, condition_id} と失敗要素の形が異なるため、
// 双方を含む superset を受け取り、存在するフィールドだけを連結して表示する。
// 詳細は execplans/2026-06-09-api-error-catalog.md §1.3（部分成功 P1〜P4）、
// レスポンス契約は execplans/2026-07-24-issue-34-registration-response-contract.md（issue #34）。
export type FailedItemLike = {
    type?: string | null;
    unit_id?: string | null;
    gear?: string | null;
    gear_id?: string | null;
    condition_id?: string | null;
    reason?: string | null;
    detail?: string | null;
};

// BE が failed_items[].reason に載せるコード語彙（issue #34）をユーザー向け日本語へ変換する。
// 未知のコードは detail をそのまま表示するフォールバック（issue の必須要件）。
const REASON_JA: Record<string, string> = {
    // copy 系共通（R/L）
    'source_data_incomplete': 'コピー元のデータが一部しかありません',
    'write_failed': 'コピー先への書き込みに失敗しました',
    'internal_error': '想定外のエラーが発生しました',
    // excel 系（R 一括 Excel / L 全経路の excel 方式）
    'excel_not_found': 'Excel ファイルが見つかりません',
    'excel_unreadable': 'Excel ファイルを読み取れません',
    'sheet_not_found': '対象のシートが見つかりません',
    'empty_sheet': 'シートにデータがありません',
    'invalid_columns': '列の形式が不正です',
    'invalid_freq': '周波数データが不正です',
    'invalid_data': 'データの値が不正です',
};

// reason が既知コードなら日本語文言、未知コードは detail（無ければ reason 自体）を返す。
export const describeReason = (reason?: string | null, detail?: string | null): string | null => {
    if (!reason) return detail ?? null;
    const known = REASON_JA[reason.trim().toLowerCase()];
    if (known) return detail ? `${known}（${detail}）` : known;
    return detail ?? reason;
};

// 失敗要素 1 件を「Gear: x / Unit: y / Condition: z / データ: t（理由）」形式の文字列にする。
export const describeFailedItem = (item: FailedItemLike): string => {
    const parts: string[] = [];
    const gear = item.gear ?? item.gear_id;
    if (gear) parts.push(`Gear: ${gear}`);
    if (item.unit_id) parts.push(`Unit: ${item.unit_id}`);
    if (item.condition_id) parts.push(`Condition: ${item.condition_id}`);
    if (item.type) parts.push(`データ: ${item.type}`);
    const head = parts.length > 0 ? parts.join(' / ') : '対象不明';
    const reason = describeReason(item.reason, item.detail);
    if (!reason) return head;
    // describeReasonは既知reasonコード+detailの場合、自身で「既知文言（detail）」の形に
    // 括弧を付けて返す（describeReason単体の表示契約）。その結果をここでさらに括弧で包むと
    // 二重括弧（「...（既知文言（detail））」）になってしまうため、既に括弧で閉じている
    // 場合はそのまま連結する（/code-review xhigh所見）。
    return reason.endsWith('）') ? `${head} ${reason}` : `${head}（${reason}）`;
};

// summary（succeeded/failed/skipped。excel 系は skipped 無し=2分類）から結果種別を判定する
// （issue #34 の 4 分類 message 表 + 全 skipped 特例に対応）。
export type BatchSummary = { succeeded: number; failed: number; skipped?: number };
export type BatchResultKind = 'all_success' | 'partial_failure' | 'all_failure' | 'all_skipped';

export const classifySummary = (summary: BatchSummary): BatchResultKind => {
    const { succeeded, failed } = summary;
    if (failed > 0 && succeeded > 0) return 'partial_failure';
    if (failed > 0 && succeeded === 0) return 'all_failure';
    if (failed === 0 && succeeded === 0) return 'all_skipped';
    return 'all_success';
};

// summary から日本語の結果メッセージを組み立てる。成功件数を明示し、
// 全 skipped（succeeded=0 かつ failed=0）はコピー元にデータが無かった旨を区別表示する。
export const buildResultMessage = (summary: BatchSummary): string => {
    switch (classifySummary(summary)) {
        case 'all_success':
            return `${summary.succeeded}件のデータを登録しました。`;
        case 'partial_failure':
            return `${summary.succeeded}件のデータを登録しました。${summary.failed}件が失敗しました。`;
        case 'all_failure':
            return `登録に失敗しました（${summary.failed}件）。`;
        case 'all_skipped':
            return 'コピー元にデータがありませんでした。';
    }
};

// コピー/Excel 登録の非2xxエラーを日本語文言へ変換する（R/L の ProjectCopy.tsx に
// 重複定義されていたロジックを集約。issue #34 で追加された新しい 400 文言を含む）。
// L の入力チェック失敗は 400 → 422（issue #52）。422 も 400 と同じ分岐で既知 message を照合し、
// 当たらなければ BE の英語定型文を出さず汎用の入力不正文言にする（400 の最終フォールバックは R と共有のため不変）。
export const buildCopyErrorMessage = (response: { status: number; message?: string }): string => {
    if (response.status === 404) {
        if (response.message?.includes("Excel file not found")) return "コピー元の Excel ファイルが見つかりません。";
        if (response.message?.includes("Unit id not found")) return "指定した Unit ID が計算セットに存在しません。";
        if (response.message?.includes("Copy source")) return "コピー元の計算セットが見つかりません。";
        return "計算セットが見つかりません。";
    }
    if (response.status === 400 || response.status === 422) {
        if (response.message?.includes("Cross powertrain")) return "BEV と HEV 間のコピーはできません。";
        if (response.message?.includes("Ambiguous")) return "コピー元の計算セットは BEV と HEV 両方に存在します。担当者に確認してください。";
        if (response.message?.includes("Mount configuration mismatch")) return "コピー元とコピー先のマウント数（mtg）が一致していません。";
        if (response.message?.includes("Acoustic property mismatch")) return "音響条件と振動条件の間ではコピーできません。";
        if (response.message?.includes("Copy source and destination are identical")) return "コピー元とコピー先が同一です。";
        if (response.message?.includes("cannot be copied per condition")) return "NTF・諸元は条件単位でコピーできません。Common コピーを利用してください。";
    }
    if (response.status === 422) return INVALID_INPUT_MESSAGE;
    return response.message || `コピー処理に失敗しました。ステータスコード: ${response.status}`;
};

// 音響性不一致(400)の message から不一致宛先 condition_id を抽出する（issue #36）。
// 契約上の文末形式: "... (source: <cid>, mismatched: <cid1>, <cid2>, ... (+N more))"。
// この形式から外れる場合は null を返し、呼び出し側は buildCopyErrorMessage の汎用文言表示のみにフォールバックする
// （BE のメッセージ文言が変わっても表示が壊れないようにするための安全策）。
export const extractAcousticMismatchIds = (
    message?: string | null,
): { ids: string[]; remainingCount: number } | null => {
    if (!message || !message.includes("Acoustic property mismatch")) return null;
    // 末尾の閉じ括弧に厳密固定すると、文末に補足文が付く・改行を含む等の軽微な文言差でも
    // 抽出できなくなる。「mismatched:」以降でメッセージ中最後の ")" までを許容してキャプチャする。
    const tailMatch = message.match(/mismatched:\s*([\s\S]+)\)/);
    if (!tailMatch) return null;

    let tail = tailMatch[1].trim();
    let remainingCount = 0;
    const moreMatch = tail.match(/^(.*?),?\s*\.\.\.\s*\(\+(\d+)\s*more\)$/);
    if (moreMatch) {
        tail = moreMatch[1].trim();
        remainingCount = Number(moreMatch[2]);
    }

    const ids = tail.split(',').map((id) => id.trim()).filter(Boolean);
    if (ids.length === 0) return null;
    return { ids, remainingCount };
};

type Props = {
    items: FailedItemLike[];
};

export const FailedItemsList: React.FC<Props> = ({ items }) => {
    if (items.length === 0) return null;
    return (
        <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
            {items.map((item, index) => (
                <Typography
                    component="li"
                    variant="body2"
                    key={`${item.gear ?? item.gear_id ?? ''}-${item.unit_id ?? ''}-${item.condition_id ?? item.type ?? ''}-${index}`}
                >
                    {describeFailedItem(item)}
                </Typography>
            ))}
        </Box>
    );
};
